/**
 * Contract Urgency Utility Functions
 * US-CONT-005: View Expiring Contracts Alert
 * Calculates urgency levels and metadata based on days remaining
 */

import { differenceInDays, parseISO, isPast } from 'date-fns';
import type {
  ContractUrgency,
  UrgencyMetadata,
  ExpiringContract,
  ExpiringContractWithUrgency
} from '../types/expiringContracts.types';
import {
  URGENCY_THRESHOLDS,
  URGENCY_COLORS,
  URGENCY_LABELS
} from '../constants/expiringContracts.constants';

/**
 * Calculate days remaining until contract expiry
 * @param endDate - Contract end date (ISO 8601 string)
 * @returns Number of days remaining (negative if expired)
 */
export function calculateDaysRemaining(endDate: string): number {
  const today = new Date();
  const expiryDate = parseISO(endDate);
  return differenceInDays(expiryDate, today);
}

/**
 * Determine urgency level based on days remaining
 * @param daysRemaining - Number of days until expiry
 * @returns Contract urgency level
 */
export function getUrgencyLevel(daysRemaining: number): ContractUrgency {
  if (daysRemaining < 0) {
    return 'EXPIRED';
  }

  if (daysRemaining <= URGENCY_THRESHOLDS.CRITICAL) {
    return 'CRITICAL';
  }

  if (daysRemaining <= URGENCY_THRESHOLDS.WARNING) {
    return 'WARNING';
  }

  return 'NORMAL';
}

/**
 * Get color scheme for urgency level
 * @param urgency - Contract urgency level
 * @returns Color scheme object with badge, text, bg, and border colors
 */
export function getUrgencyColors(urgency: ContractUrgency) {
  return URGENCY_COLORS[urgency];
}

/**
 * Get display label for urgency level
 * @param urgency - Contract urgency level
 * @returns Display label string
 */
export function getUrgencyLabel(urgency: ContractUrgency): string {
  return URGENCY_LABELS[urgency];
}

/**
 * Check if contract is expiring soon (within threshold)
 * @param daysRemaining - Number of days until expiry
 * @param threshold - Days threshold (default: 30)
 * @returns True if contract is expiring soon
 */
export function isExpiringSoon(daysRemaining: number, threshold: number = 30): boolean {
  return daysRemaining >= 0 && daysRemaining <= threshold;
}

/**
 * Check if contract is expired
 * @param endDate - Contract end date (ISO 8601 string)
 * @returns True if contract is expired
 */
export function isExpired(endDate: string): boolean {
  return isPast(parseISO(endDate));
}

/**
 * Calculate complete urgency metadata for a contract
 * @param endDate - Contract end date (ISO 8601 string)
 * @returns Complete urgency metadata object
 */
export function calculateContractUrgency(endDate: string): UrgencyMetadata {
  const daysRemaining = calculateDaysRemaining(endDate);
  const urgencyLevel = getUrgencyLevel(daysRemaining);
  const colorScheme = getUrgencyColors(urgencyLevel);
  const label = getUrgencyLabel(urgencyLevel);
  const isExpiringSoonFlag = isExpiringSoon(daysRemaining);

  return {
    daysRemaining,
    urgencyLevel,
    colorScheme,
    label,
    isExpiringSoon: isExpiringSoonFlag
  };
}

/**
 * Enrich contract with urgency metadata
 * @param contract - Base contract object
 * @returns Contract with calculated urgency metadata
 */
export function enrichContractWithUrgency(
  contract: ExpiringContract
): ExpiringContractWithUrgency {
  const urgency = calculateContractUrgency(contract.endDate);

  return {
    ...contract,
    urgency
  };
}

/**
 * Enrich multiple contracts with urgency metadata
 * @param contracts - Array of base contracts
 * @returns Array of contracts with urgency metadata
 */
export function enrichContractsWithUrgency(
  contracts: ExpiringContract[]
): ExpiringContractWithUrgency[] {
  return contracts.map(enrichContractWithUrgency);
}

/**
 * Sort contracts by urgency (most urgent first)
 * @param contracts - Array of contracts with urgency
 * @returns Sorted array (EXPIRED → CRITICAL → WARNING → NORMAL)
 */
export function sortByUrgency(
  contracts: ExpiringContractWithUrgency[]
): ExpiringContractWithUrgency[] {
  const urgencyOrder: Record<ContractUrgency, number> = {
    EXPIRED: 0,
    CRITICAL: 1,
    WARNING: 2,
    NORMAL: 3
  };

  return [...contracts].sort((a, b) => {
    const urgencyDiff = urgencyOrder[a.urgency.urgencyLevel] - urgencyOrder[b.urgency.urgencyLevel];

    // If same urgency, sort by days remaining (ascending)
    if (urgencyDiff === 0) {
      return a.urgency.daysRemaining - b.urgency.daysRemaining;
    }

    return urgencyDiff;
  });
}

/**
 * Filter contracts by urgency level
 * @param contracts - Array of contracts with urgency
 * @param urgencyLevels - Array of urgency levels to filter by
 * @returns Filtered array
 */
export function filterByUrgency(
  contracts: ExpiringContractWithUrgency[],
  urgencyLevels: ContractUrgency[]
): ExpiringContractWithUrgency[] {
  if (!urgencyLevels || urgencyLevels.length === 0) {
    return contracts;
  }

  return contracts.filter(contract =>
    urgencyLevels.includes(contract.urgency.urgencyLevel)
  );
}

/**
 * Get contracts by specific urgency level
 * @param contracts - Array of contracts with urgency
 * @param urgencyLevel - Specific urgency level
 * @returns Filtered array
 */
export function getContractsByUrgency(
  contracts: ExpiringContractWithUrgency[],
  urgencyLevel: ContractUrgency
): ExpiringContractWithUrgency[] {
  return contracts.filter(
    contract => contract.urgency.urgencyLevel === urgencyLevel
  );
}

/**
 * Count contracts by urgency level
 * @param contracts - Array of contracts with urgency
 * @returns Object with counts by urgency level
 */
export function countByUrgency(contracts: ExpiringContractWithUrgency[]): Record<ContractUrgency, number> {
  return contracts.reduce(
    (counts, contract) => {
      counts[contract.urgency.urgencyLevel]++;
      return counts;
    },
    {
      EXPIRED: 0,
      CRITICAL: 0,
      WARNING: 0,
      NORMAL: 0
    } as Record<ContractUrgency, number>
  );
}

/**
 * Get urgency icon name (for icon libraries)
 * @param urgency - Contract urgency level
 * @returns Icon name string
 */
export function getUrgencyIcon(urgency: ContractUrgency): string {
  const iconMap: Record<ContractUrgency, string> = {
    EXPIRED: 'alert-circle',
    CRITICAL: 'alert-triangle',
    WARNING: 'alert-octagon',
    NORMAL: 'check-circle'
  };

  return iconMap[urgency];
}
